import React, { useState, ReactNode, useCallback, useEffect, useRef } from 'react';
import { Container } from './Wizard.styles';

export interface WizardStepProps {
    onNext?: () => void;
    onPrevious?: () => void;
    isFirstStep?: boolean;
    isLastStep?: boolean;
    [key: string]: any; // Allow additional props to be passed
  }

// Types for the component props
export interface WizardProps {
  steps: ReactNode[];
  initialStep?: number;
  onFinish?: () => void;
  onStepChange?: (step: number) => void;
}

// Types for the hook return value
interface UseWizardReturn {
  currentStep: number;
  next: () => void;
  previous: () => void;
  goTo: (step: number) => void;
  isFirstStep: boolean;
  isLastStep: boolean;
  totalSteps: number;
}

// Custom hook for tracking the current position
export const useWizard = (totalSteps: number, initialStep = 0): UseWizardReturn => {
  const [currentStep, setCurrentStep] = useState(
    initialStep >= 0 && initialStep < totalSteps ? initialStep : 0
  );

  const isFirstStep = currentStep === 0;
  const isLastStep = currentStep === totalSteps - 1;

  const next = useCallback(() => {
    if (currentStep < totalSteps - 1) {
      setCurrentStep(prevStep => prevStep + 1);
    }
  }, [currentStep, totalSteps]);

  const previous = useCallback(() => {
    if (currentStep > 0) {
      setCurrentStep(prevStep => prevStep - 1);
    }
  }, [currentStep]);

  const goTo = useCallback((step: number) => {
    if (step >= 0 && step < totalSteps) {
      setCurrentStep(step);
    }
  }, [totalSteps]);

  return {
    currentStep,
    next,
    previous,
    goTo,
    isFirstStep,
    isLastStep,
    totalSteps
  };
};

// The Wizard component
export const Wizard: React.FC<WizardProps> = ({
  steps,
  initialStep = 0,
  onFinish,
  onStepChange
}) => {
  // Use internal state management
  const [activeStep, setActiveStep] = useState(initialStep);
  const isTransitioning = useRef(false);

  const isFirstStep = activeStep === 0;
  const isLastStep = activeStep === steps.length - 1;

  // Handle navigation with debounce to prevent rapid state changes
  const next = useCallback(() => {
    if (activeStep < steps.length - 1 && !isTransitioning.current) {
      isTransitioning.current = true;
      setActiveStep(prev => prev + 1);
      if (onStepChange) {
        onStepChange(activeStep + 1);
      }
      // Reset the transitioning flag after a short delay
      setTimeout(() => {
        isTransitioning.current = false;
      }, 50);
    }
  }, [activeStep, steps.length, onStepChange]);

  const previous = useCallback(() => {
    if (activeStep > 0 && !isTransitioning.current) {
      isTransitioning.current = true;
      setActiveStep(prev => prev - 1);
      if (onStepChange) {
        onStepChange(activeStep - 1);
      }
      // Reset the transitioning flag after a short delay
      setTimeout(() => {
        isTransitioning.current = false;
      }, 50);
    }
  }, [activeStep, onStepChange]);

  // This will call onFinish when the user navigates beyond the last step
  const handleNext = useCallback(() => {
    if (isLastStep) {
      onFinish?.();
    } else {
      next();
    }
  }, [isLastStep, next, onFinish]);

  // Update internal state if initialStep prop changes
  useEffect(() => {
    if (!isTransitioning.current) {
      setActiveStep(initialStep);
    }
  }, [initialStep]);

  if (!steps.length) {
    return <Container />;
  }

  // Get the current step component
  const CurrentStep = steps[activeStep];

  return (
    <Container>
      {React.isValidElement(CurrentStep) ?
        React.cloneElement(CurrentStep, {
          onNext: handleNext,
          onPrevious: previous,
          isFirstStep,
          isLastStep
        } as WizardStepProps) :
        CurrentStep
      }
    </Container>
  );
};

// Export the hook for external use
export const useWizardWithSteps = (steps: ReactNode[], initialStep = 0): UseWizardReturn & {
  handleFinish: () => void;
} => {
  const wizard = useWizard(steps.length, initialStep);

  const handleFinish = useCallback(() => {
    if (wizard.isLastStep) {
      // This would typically trigger whatever completion logic is needed
    } else {
      wizard.next();
    }
  }, [wizard]);

  return {
    ...wizard,
    handleFinish
  };
};

// Export both the component and hooks
export default Wizard;
